import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ProductCard from '../components/ProductCard';
import PromoSlot, { useCountdown } from '../components/PromoBanner';
import PriceTag from '../components/PriceTag';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { withDefaults, SECTION_DEFAULTS } from '../lib/homeSections';
import { getRecentlyViewed } from '../lib/recentlyViewed';
import { needsChoices } from '../lib/cartLine';
import {
  Search, Star, ChevronRight, ChevronLeft, X, Mail, Flame, Layers, Tag, Truck, ShieldCheck, Sparkles,
  MessageCircleHeart, Clock, Trophy, Gift, Package, Plus, History, ArrowRight,
} from 'lucide-react';
import Select from '../components/FlatSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: 'Outfit, sans-serif' };

// ── Building blocks ───────────────────────────────────────────────

function useProducts(params, enabled = true) {
  const [state, setState] = useState({ products: [], loading: true });
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) { setState({ products: [], loading: false }); return; }
    axios.get(`${API}/products`, { params })
      .then(r => setState({ products: r.data.products, loading: false }))
      .catch(() => setState({ products: [], loading: false }));
  }, [key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function SectionHeader({ icon: Icon, eyebrow, title, subtitle, link, linkText = 'See all', extra, iconClass = 'text-honey-600' }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
      <div>
        {eyebrow && (
          <p className={`flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-black mb-1 ${iconClass}`}>
            {Icon && <Icon className="w-4 h-4" />} {eyebrow}
          </p>
        )}
        <h2 className="text-2xl sm:text-3xl font-black text-ink" style={heading}>{title}</h2>
        {subtitle && <p className="text-ink/55 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-4">
        {extra}
        {link && (
          link.startsWith('#') || link.startsWith('http')
            ? <a href={link} className="flex items-center gap-1 text-ink font-bold text-sm hover:gap-2 transition-all">{linkText} <ChevronRight className="w-4 h-4" /></a>
            : <Link to={link} className="flex items-center gap-1 text-ink font-bold text-sm hover:gap-2 transition-all">{linkText} <ChevronRight className="w-4 h-4" /></Link>
        )}
      </div>
    </div>
  );
}

function Section({ id, children, band = '', testId }) {
  return (
    <section id={id} className={`px-4 sm:px-6 py-10 ${band}`} data-testid={testId}>
      <div className="max-w-7xl mx-auto">{children}</div>
    </section>
  );
}

/** Horizontally scrolling row of product cards with arrow buttons. */
function ProductRail({ products, loading, ranked = false }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: 'smooth' });
  if (loading) {
    return <div className="flex gap-4 overflow-hidden">{[...Array(5)].map((_, i) => <div key={i} className="w-56 h-80 flex-shrink-0 rounded-2xl bg-sand animate-pulse" />)}</div>;
  }
  return (
    <div className="relative group/rail">
      <div ref={ref} className="flex gap-4 overflow-x-auto scroll-rail snap-x pb-2">
        {products.map((p, i) => <div key={p.id} className="snap-start"><ProductCard product={p} compact rank={ranked ? i + 1 : undefined} /></div>)}
      </div>
      {products.length > 4 && (
        <>
          <button onClick={() => scroll(-1)} aria-label="Scroll left"
            className="hidden md:flex absolute -left-4 top-1/3 w-10 h-10 rounded-full bg-white shadow-lg border border-ink/10 items-center justify-center opacity-0 group-hover/rail:opacity-100 transition-opacity"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} aria-label="Scroll right"
            className="hidden md:flex absolute -right-4 top-1/3 w-10 h-10 rounded-full bg-white shadow-lg border border-ink/10 items-center justify-center opacity-0 group-hover/rail:opacity-100 transition-opacity"><ChevronRight className="w-5 h-5" /></button>
        </>
      )}
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────

function HeroSection({ config, sales }) {
  const { products: newest } = useProducts({ limit: 1, sort: 'newest' });
  const sale = sales[0];
  const saleCountdown = useCountdown(sale?.ends_at);
  const cta = config.cta_link || '#products';
  const CtaTag = cta.startsWith('#') || cta.startsWith('http') ? 'a' : Link;
  const ctaProps = CtaTag === 'a' ? { href: cta } : { to: cta };

  return (
    <section className="px-4 sm:px-6 pt-6 pb-4">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-gradient-to-br from-honey-100 via-cream to-white border border-honey/30 min-h-[340px] flex items-center">
          <div className="absolute -right-16 -bottom-20 w-80 h-80 rounded-full bg-honey/25" />
          <div className="absolute right-24 -top-10 w-40 h-40 rounded-full bg-coral/10" />
          {config.image && (
            <img src={config.image} alt="" className="absolute right-0 bottom-0 h-full w-1/2 object-cover object-center hidden sm:block [mask-image:linear-gradient(to_right,transparent,black_35%)]" />
          )}
          <div className="relative p-8 sm:p-12 max-w-xl">
            {config.badge_text && (
              <span className="inline-flex items-center gap-1.5 bg-white text-ink text-xs font-black px-3 py-1.5 rounded-full shadow-sm mb-5">
                <Sparkles className="w-3.5 h-3.5 text-honey-600" /> {config.badge_text}
              </span>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-ink leading-[1.05] mb-4" style={heading}>
              {config.title_line1}
              {config.title_line2 && <><br /><span className="relative inline-block">
                <span className="relative z-10">{config.title_line2}</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 sm:h-4 bg-honey/60 -z-0 rounded" />
              </span></>}
            </h1>
            {config.description && <p className="text-ink/70 text-lg mb-7">{config.description}</p>}
            <div className="flex flex-wrap gap-3">
              <CtaTag {...ctaProps} className="btn-primary flex items-center gap-2" data-testid="hero-cta">{config.cta_text || 'Start shopping'} <ArrowRight className="w-4 h-4" /></CtaTag>
              {sale && <Link to="/sale" className="btn-secondary">Shop the sale</Link>}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          {sale ? (
            <Link to="/sale" className="relative overflow-hidden rounded-3xl bg-coral text-white p-6 flex flex-col justify-between min-h-[160px] hover:shadow-xl transition-shadow" data-testid="hero-sale-tile">
              <Flame className="absolute -right-4 -bottom-4 w-32 h-32 text-white/15" />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-black text-white/80 mb-1">On sale now</p>
                <p className="text-2xl font-black leading-tight" style={heading}>{sale.name}</p>
                <p className="font-bold">{sale.discount_type === 'percent' ? `${sale.discount_value}% off` : `$${sale.discount_value} off`}{sale.applies_to === 'categories' && sale.categories?.length ? ` ${sale.categories.slice(0, 2).join(' & ')}` : ''}</p>
              </div>
              <p className="relative flex items-center gap-1.5 text-sm font-bold"><Clock className="w-4 h-4" /> {saleCountdown ? `Ends in ${saleCountdown}` : 'Limited time'}</p>
            </Link>
          ) : (
            <a href="#best-sellers" className="relative overflow-hidden rounded-3xl bg-ink text-white p-6 flex flex-col justify-between min-h-[160px]">
              <Trophy className="absolute -right-4 -bottom-4 w-32 h-32 text-white/10" />
              <p className="text-xs uppercase tracking-[0.2em] font-black text-honey">Trending</p>
              <p className="text-2xl font-black leading-tight" style={heading}>See what everyone's buying</p>
              <p className="flex items-center gap-1 text-sm font-bold text-honey">Best sellers <ChevronRight className="w-4 h-4" /></p>
            </a>
          )}
          <a href="#new-arrivals" className="relative overflow-hidden rounded-3xl bg-white border border-ink/10 p-6 flex flex-col justify-between min-h-[160px] hover:border-honey transition-colors">
            {newest[0]?.images?.[0] && <img src={newest[0].images[0]} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover [mask-image:linear-gradient(to_right,transparent,black_40%)]" />}
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.2em] font-black text-honey-700 mb-1">Just landed</p>
              <p className="text-2xl font-black leading-tight text-ink max-w-[60%]" style={heading}>New arrivals</p>
            </div>
            <p className="relative flex items-center gap-1 text-sm font-bold text-ink">Take a look <ChevronRight className="w-4 h-4" /></p>
          </a>
        </div>
      </div>
    </section>
  );
}

function TrustBar({ config, freeShipping }) {
  const items = [
    [Truck, freeShipping ? `Free shipping over $${freeShipping}` : config.item1],
    [ShieldCheck, config.item2], [Sparkles, config.item3], [MessageCircleHeart, config.item4],
  ].filter(([, t]) => t);
  return (
    <section className="px-4 sm:px-6 py-4">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(([Icon, text]) => (
          <div key={text} className="flex items-center gap-3 bg-white border border-ink/10 rounded-2xl px-4 py-3">
            <div className="w-9 h-9 rounded-full bg-honey-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-honey-700" /></div>
            <p className="text-ink text-sm font-semibold leading-tight">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CategoryGrid({ config }) {
  const [categories, setCategories] = useState(null);
  useEffect(() => {
    axios.get(`${API}/products/categories-detailed`).then(r => setCategories(r.data.categories)).catch(() => setCategories([]));
  }, []);
  if (categories && categories.length === 0) return null;
  const TINTS = ['bg-honey-100', 'bg-coral-50', 'bg-[#E8F5EE]', 'bg-[#EAF0FB]', 'bg-[#F3ECFA]', 'bg-sand'];
  return (
    <Section id="categories" testId="category-grid">
      <SectionHeader eyebrow="Browse" title={config.title} subtitle={config.subtitle} />
      <div className="flex gap-4 overflow-x-auto scroll-rail pb-2">
        {(categories || [...Array(6)]).map((cat, i) => cat ? (
          <Link key={cat.name} to={`/?category=${encodeURIComponent(cat.name)}`} data-testid={`category-card-${cat.name}`}
            className="group flex-shrink-0 w-28 sm:w-32 text-center">
            <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden ${TINTS[i % TINTS.length]} border-4 border-white shadow-sm group-hover:shadow-lg group-hover:-translate-y-1 transition-all mb-2`}>
              {cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                : <Tag className="w-10 h-10 text-ink/20 m-auto mt-10" />}
            </div>
            <p className="text-ink font-bold text-sm leading-tight group-hover:text-honey-700">{cat.name}</p>
            <p className="text-ink/45 text-xs">{cat.count} item{cat.count !== 1 ? 's' : ''}</p>
          </Link>
        ) : <div key={i} className="w-32 h-40 flex-shrink-0 rounded-2xl bg-sand animate-pulse" />)}
      </div>
    </Section>
  );
}

function DealsSection({ config, sales }) {
  const { products, loading } = useProducts({ sale: true, limit: config.max_items || 10 });
  const countdown = useCountdown(sales[0]?.ends_at);
  if (!loading && products.length === 0) return null;
  return (
    <Section id="deals" band="bg-gradient-to-b from-coral-50 to-transparent" testId="deals-section">
      <SectionHeader icon={Flame} iconClass="text-coral-600" eyebrow="Limited time" title={config.title} subtitle={config.subtitle} link="/sale" linkText="See all deals"
        extra={countdown && (
          <span className="flex items-center gap-2 bg-coral text-white text-sm font-black px-3 py-1.5 rounded-full"><Clock className="w-4 h-4" /> Ends in {countdown}</span>
        )} />
      <ProductRail products={products} loading={loading} />
    </Section>
  );
}

function FeaturedSection({ config }) {
  const { products, loading } = useProducts({ featured: true, limit: config.max_items || 8 });
  if (!loading && products.length === 0) return null;
  return (
    <Section id="featured" testId="featured-section">
      <SectionHeader icon={Star} eyebrow={config.subtitle || 'Hand-picked'} title={config.title} link={config.show_view_all ? '#products' : null} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-sand animate-pulse" />)
          : products.map(p => <ProductCard key={p.id} product={p} />)}
      </div>
    </Section>
  );
}

function BestSellers({ config }) {
  const { products, loading } = useProducts({ sort: 'bestselling', limit: config.max_items || 10 });
  if (!loading && products.length < 3) return null; // not enough sales history yet
  return (
    <Section id="best-sellers" band="bg-sand/60" testId="best-sellers">
      <SectionHeader icon={Trophy} eyebrow="Trending" title={config.title} subtitle={config.subtitle} />
      <ProductRail products={products} loading={loading} ranked />
    </Section>
  );
}

function BundlesSection({ config }) {
  const { products: bundles, loading } = useProducts({ bundle: true, limit: 6 });
  const [deals, setDeals] = useState([]);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [busy, setBusy] = useState('');
  useEffect(() => { axios.get(`${API}/deals`).then(r => setDeals(r.data.deals)).catch(() => {}); }, []);
  if (!loading && bundles.length === 0 && deals.length === 0) return null;

  const addAll = async (deal) => {
    if (!user) { window.location.href = '/auth'; return; }
    setBusy(deal.id);
    for (const item of deal.items) { if (!(await addToCart(item.id, 1))) break; }
    setBusy('');
  };

  return (
    <Section id="bundles" testId="bundles-section">
      <SectionHeader icon={Gift} eyebrow="Save more" title={config.title} subtitle={config.subtitle} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {deals.slice(0, 4).map(deal => (
          <div key={deal.id} className="bg-white border border-ink/10 rounded-2xl p-5 flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              {deal.items.map((item, i) => (
                <React.Fragment key={item.id}>
                  {i > 0 && <Plus className="w-4 h-4 text-ink/30" />}
                  <Link to={`/products/${item.id}`} className="w-16 h-16 rounded-xl overflow-hidden bg-sand block" title={item.title}>
                    {item.images?.[0] && <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />}
                  </Link>
                </React.Fragment>
              ))}
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="text-ink font-bold">{deal.name}</p>
              <PriceTag now={deal.price} was={deal.value} className="text-sm" />
              <p className="text-green-700 text-xs font-bold mt-0.5">Save ${(deal.value - deal.price).toFixed(2)} when bought together</p>
            </div>
            {!deal.items.some(needsChoices) && (
              <button onClick={() => addAll(deal)} disabled={busy === deal.id} className="btn-primary !px-4 !py-2 text-sm">{busy === deal.id ? 'Adding…' : 'Add all'}</button>
            )}
          </div>
        ))}
        {bundles.map(b => (
          <Link key={b.id} to={`/products/${b.id}`} className="group bg-gradient-to-br from-honey-50 to-white border border-honey/40 rounded-2xl p-5 flex items-center gap-5 hover:shadow-lg transition-shadow">
            <div className="w-24 h-24 rounded-xl overflow-hidden bg-sand flex-shrink-0">
              {b.images?.[0] ? <img src={b.images[0]} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <Package className="w-10 h-10 text-ink/20 m-auto mt-7" />}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[11px] font-black uppercase tracking-wider bg-honey text-ink px-2 py-0.5 rounded-full">Bundle</span>
              <p className="text-ink font-bold mt-1 truncate">{b.title}</p>
              <p className="text-ink/55 text-xs truncate">{b.bundle.items.map(i => `${i.quantity > 1 ? i.quantity + '× ' : ''}${i.title}`).join(' + ')}</p>
              <PriceTag now={b.sale?.price ?? b.price} was={b.bundle.value > (b.sale?.price ?? b.price) ? b.bundle.value : null} className="text-sm" />
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function NewArrivals({ config }) {
  const { products, loading } = useProducts({ sort: 'newest', limit: config.max_items || 12 });
  if (!loading && products.length === 0) return null;
  return (
    <Section id="new-arrivals" testId="new-arrivals">
      <SectionHeader icon={Sparkles} eyebrow="New" title={config.title} subtitle={config.subtitle} link="#products" />
      <ProductRail products={products} loading={loading} />
    </Section>
  );
}

function TopRated({ config }) {
  const { products, loading } = useProducts({ sort: 'rating', min_rating: 4, limit: config.max_items || 10 });
  if (!loading && products.length < 2) return null;
  return (
    <Section id="top-rated" band="bg-honey-50/60" testId="top-rated">
      <SectionHeader icon={Star} eyebrow="Customer favorites" title={config.title} subtitle={config.subtitle} />
      <ProductRail products={products} loading={loading} />
    </Section>
  );
}

function RecentlyViewed({ config }) {
  const ids = getRecentlyViewed();
  const { products, loading } = useProducts({ ids: ids.join(',') }, ids.length > 0);
  if (!ids.length || (!loading && products.length === 0)) return null;
  return (
    <Section id="recently-viewed" testId="recently-viewed">
      <SectionHeader icon={History} eyebrow="Pick up where you left off" title={config.title} />
      <ProductRail products={products} loading={loading} />
    </Section>
  );
}

function CountdownBanner({ config }) {
  const countdown = useCountdown(config.end_date);
  if (!config.end_date || !countdown) return null;
  const cta = config.cta_link || '/sale';
  return (
    <Section testId="countdown-banner">
      <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="absolute -left-10 -top-10 w-48 h-48 rounded-full bg-honey/20" />
        <div className="relative text-center md:text-left">
          <p className="flex items-center gap-2 justify-center md:justify-start text-honey text-xs uppercase tracking-[0.25em] font-black mb-2"><Flame className="w-4 h-4" /> Limited time</p>
          <h2 className="text-3xl md:text-4xl font-black" style={heading}>{config.title}</h2>
          {config.subtitle && <p className="text-white/70">{config.subtitle}</p>}
        </div>
        <p className="relative text-2xl md:text-3xl font-black font-mono text-honey">{countdown}</p>
        {cta.startsWith('/') ? <Link to={cta} className="relative btn-primary">{config.cta_text || 'Shop now'}</Link>
          : <a href={cta} className="relative btn-primary">{config.cta_text || 'Shop now'}</a>}
      </div>
    </Section>
  );
}

function CollectionModal({ collection, onClose }) {
  const ids = (collection.product_ids || []).slice(0, 24);
  const { products, loading } = useProducts({ ids: ids.join(',') }, ids.length > 0);
  return (
    <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8 px-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} onClick={e => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-5xl overflow-hidden shadow-2xl">
        {collection.image && <img src={collection.image} alt={collection.title} className="w-full h-48 object-cover" />}
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-2xl font-black text-ink" style={heading}>{collection.title}</h3>
              {collection.description && <p className="text-ink/60">{collection.description}</p>}
            </div>
            <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-6 h-6" /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-72 rounded-2xl bg-sand animate-pulse" />) : products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CollectionsSection({ config }) {
  const [collections, setCollections] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => { axios.get(`${API}/collections`).then(r => setCollections(r.data.collections)).catch(() => setCollections([])); }, []);
  if (!collections || collections.length === 0) return null;
  return (
    <Section id="collections" testId="collections-section">
      <SectionHeader icon={Layers} eyebrow="Collections" title={config.title} subtitle={config.subtitle} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map(col => (
          <button key={col.id} onClick={() => setSelected(col)} data-testid={`collection-card-${col.id}`}
            className="group text-left bg-white border border-ink/10 rounded-2xl overflow-hidden hover:shadow-lg hover:border-honey/60 transition-all">
            <div className="h-44 bg-sand overflow-hidden">
              {col.image ? <img src={col.image} alt={col.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : (
                <div className="grid grid-cols-4 gap-1 h-full p-2">
                  {(col.preview_products || []).slice(0, 4).map((p, j) => <div key={j} className="rounded-lg overflow-hidden bg-white">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>)}
                </div>
              )}
            </div>
            <div className="p-5">
              <p className="text-ink font-bold text-lg" style={heading}>{col.title}</p>
              {col.description && <p className="text-ink/55 text-sm line-clamp-2">{col.description}</p>}
              <p className="flex items-center gap-1 text-honey-700 text-sm font-bold mt-2 group-hover:gap-2 transition-all">{col.product_count} items <ChevronRight className="w-4 h-4" /></p>
            </div>
          </button>
        ))}
      </div>
      <AnimatePresence>{selected && <CollectionModal collection={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
    </Section>
  );
}

function ProductsSection({ config }) {
  const [params, setParams] = useSearchParams();
  const urlSearch = params.get('q') || '';
  const urlCategory = params.get('category') || '';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingSearch, setPendingSearch] = useState(urlSearch);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const sectionRef = useRef(null);
  const LIMIT = 16;

  useEffect(() => { axios.get(`${API}/products/categories`).then(r => setCategories(r.data.categories)).catch(() => {}); }, []);
  useEffect(() => { setPendingSearch(urlSearch); setPage(1); }, [urlSearch, urlCategory]);
  useEffect(() => {
    if (urlSearch || urlCategory) sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [urlSearch, urlCategory]);
  useEffect(() => {
    setLoading(true);
    const q = { page, limit: LIMIT, sort };
    if (urlCategory) q.category = urlCategory;
    if (urlSearch) q.search = urlSearch;
    axios.get(`${API}/products`, { params: q })
      .then(r => { setProducts(prev => (page === 1 ? r.data.products : [...prev, ...r.data.products])); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, sort, urlSearch, urlCategory]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const title = urlSearch ? `Results for "${urlSearch}"` : urlCategory || config.title;

  return (
    <Section id="products" testId="products-section">
      <div ref={sectionRef} className="scroll-mt-32" />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <h2 className="text-2xl sm:text-3xl font-black text-ink" style={heading}>{title} <span className="text-ink/40 text-lg font-bold">({total})</span></h2>
        <div className="flex items-center gap-3">
          {config.show_search && (
            <form onSubmit={e => { e.preventDefault(); setFilter('q', pendingSearch.trim()); }} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
              <input value={pendingSearch} onChange={e => setPendingSearch(e.target.value)} placeholder="Filter…" data-testid="search-input"
                className="bg-white border border-ink/15 rounded-full pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:border-honey w-44" />
            </form>
          )}
          <Select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
            className="bg-white border border-ink/15 rounded-full px-4 py-2 text-sm text-ink focus:outline-none focus:border-honey">
            <option value="newest">Newest</option><option value="bestselling">Best selling</option><option value="rating">Top rated</option>
            <option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option>
          </Select>
        </div>
      </div>

      {config.show_categories && categories.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-6">
          {['', ...categories].map(cat => (
            <button key={cat || 'all'} onClick={() => setFilter('category', cat)} data-testid={cat ? `category-${cat}` : 'category-all'}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${urlCategory === cat ? 'bg-ink text-white' : 'bg-white text-ink/70 border border-ink/15 hover:border-ink/40'}`}>
              {cat || 'All'}
            </button>
          ))}
          {(urlSearch || urlCategory) && (
            <button onClick={() => setParams({}, { replace: true })} className="flex items-center gap-1 px-3 py-2 text-sm text-coral-600 font-semibold"><X className="w-4 h-4" /> Clear</button>
          )}
        </div>
      )}

      {loading && page === 1 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-sand animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-ink/10">
          <Search className="w-10 h-10 text-ink/20 mx-auto mb-3" />
          <p className="text-ink/60 text-lg">No products found</p>
          {(urlSearch || urlCategory) && <button onClick={() => setParams({}, { replace: true })} className="text-honey-700 font-bold text-sm mt-2 hover:underline">Clear filters</button>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{products.map(p => <ProductCard key={p.id} product={p} />)}</div>
          {products.length < total && (
            <div className="text-center mt-10">
              <button onClick={() => setPage(p => p + 1)} disabled={loading} className="btn-secondary">{loading ? 'Loading…' : `Show more (${total - products.length} left)`}</button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function NewsletterSection({ config }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus(r.data.already_subscribed ? 'existing' : 'success');
      setMsg(r.data.message);
    } catch (err) { setStatus('error'); setMsg(err.response?.data?.detail || 'Something went wrong'); }
  };
  return (
    <Section id="newsletter" testId="newsletter-section">
      <div className="relative overflow-hidden rounded-3xl bg-honey p-8 md:p-14 text-center">
        <div className="absolute -left-10 -bottom-16 w-56 h-56 rounded-full bg-white/25" />
        <div className="absolute -right-8 -top-12 w-40 h-40 rounded-full bg-coral/20" />
        <div className="relative max-w-2xl mx-auto">
          {config.incentive_badge && (
            <span className="inline-flex items-center gap-2 bg-ink text-honey text-sm font-black px-4 py-1.5 rounded-full mb-5"><Star className="w-3.5 h-3.5 fill-honey" /> {config.incentive_badge}</span>
          )}
          <h2 className="text-3xl md:text-4xl font-black text-ink mb-2" style={heading}>{config.title}</h2>
          <p className="text-ink/75 mb-7">{config.subtitle}</p>
          {status === 'success' ? (
            <p className="text-ink font-bold text-lg flex items-center justify-center gap-2"><Mail className="w-5 h-5" /> You're in! {msg}</p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={config.placeholder} data-testid="newsletter-email-input"
                className="flex-1 bg-white rounded-full px-5 py-3 text-ink text-sm placeholder-ink/40 focus:outline-none focus:ring-2 focus:ring-ink" />
              <button type="submit" disabled={status === 'loading'} data-testid="newsletter-submit-btn"
                className="bg-ink text-white font-bold rounded-full px-6 py-3 hover:bg-ink/85 disabled:opacity-50">{status === 'loading' ? '…' : config.button_text}</button>
            </form>
          )}
          {status === 'error' && <p className="text-coral-700 text-sm mt-3">{msg}</p>}
          {status === 'existing' && <p className="text-ink text-sm mt-3">You're already subscribed. Thanks!</p>}
          <p className="text-ink/60 text-xs mt-4">No spam. Unsubscribe anytime.</p>
        </div>
      </div>
    </Section>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function Home() {
  const [pageConfig, setPageConfig] = useState(null);
  const [sales, setSales] = useState([]);
  const [storeInfo, setStoreInfo] = useState({});

  useEffect(() => {
    document.title = 'Market @ Buzzzillion | Shop quality goods';
    axios.get(`${API}/page-config`).then(r => setPageConfig(withDefaults(r.data))).catch(() => setPageConfig(withDefaults({})));
    axios.get(`${API}/sales/active`).then(r => setSales(r.data.sales)).catch(() => {});
    axios.get(`${API}/store-info`).then(r => setStoreInfo(r.data)).catch(() => {});
  }, []);

  if (!pageConfig) return (
    <div className="store-page flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const render = (id) => {
    const cfg = { ...SECTION_DEFAULTS[id], ...(pageConfig[id] || {}) };
    if (cfg.enabled === false) return null;
    switch (id) {
      case 'hero': return <HeroSection config={cfg} sales={sales} />;
      case 'trust_bar': return <TrustBar config={cfg} freeShipping={storeInfo.free_shipping_threshold} />;
      case 'promotions': return <div className="px-4 sm:px-6 py-4"><PromoSlot placement="home" className="max-w-7xl mx-auto" /></div>;
      case 'category_grid': return <CategoryGrid config={cfg} />;
      case 'deals_section': return <DealsSection config={cfg} sales={sales} />;
      case 'featured_section': return <FeaturedSection config={cfg} />;
      case 'best_sellers': return <BestSellers config={cfg} />;
      case 'bundles_section': return <BundlesSection config={cfg} />;
      case 'new_arrivals': return <NewArrivals config={cfg} />;
      case 'countdown_banner': return <CountdownBanner config={cfg} />;
      case 'collections': return <CollectionsSection config={cfg} />;
      case 'top_rated': return <TopRated config={cfg} />;
      case 'recently_viewed': return <RecentlyViewed config={cfg} />;
      case 'products_section': return <ProductsSection config={cfg} />;
      case 'newsletter': return <NewsletterSection config={cfg} />;
      default: return null;
    }
  };

  const bar = pageConfig.announcement_bar;
  return (
    <div className="store-page">
      {bar?.enabled && bar.text && (
        <div className="py-2 px-6 text-center text-sm font-bold" data-testid="announcement-bar"
          style={{ backgroundColor: bar.bg_color || '#1F1A14', color: bar.text_color || '#FFFFFF' }}>
          {bar.link ? <a href={bar.link} className="hover:underline">{bar.text}</a> : bar.text}
        </div>
      )}
      <Navbar />
      {pageConfig.sections_order.map(id => <React.Fragment key={id}>{render(id)}</React.Fragment>)}
      <div className="h-10" />
    </div>
  );
}
